import React, { useState } from 'react';
import {
  Alert,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl as MuiFormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DataGrid, type GridColDef, type GridValueGetterParams } from '@mui/x-data-grid';
import { Button } from 'components/button/Button';
import { DEFAULT_PAGE_SIZE } from 'utils/constants';
import {
  type IPayout,
  SellerPayoutStatus,
  useExportPayoutsMutation,
  useGetPayoutsQuery,
  useTransitionPayoutMutation
} from 'store/api/payoutApi';
import { useNotification } from 'components/notification/Notification';
import { getErrorMessage } from 'utils/errorMessage';
import './AdminPayoutsTab.scss';

// DeliveredOperationCode per OrdersProfitQuery.cs (Server) - same constant,
// duplicated here because the client has no shared constants file reaching
// across that boundary. If that constant ever changes server-side, this
// hint silently stops matching - low-stakes (it's a visual hint only, the
// server is still the one enforcing all actual transition rules).
const DELIVERED_OPERATION_CODE = -14;

const AdminPayoutsTab = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();

  const statusLabels: Record<SellerPayoutStatus, string> = {
    [SellerPayoutStatus.Owed]: t('administration.payouts.statusOwed'),
    [SellerPayoutStatus.CodReceived]: t('administration.payouts.statusCodReceived'),
    [SellerPayoutStatus.Paid]: t('administration.payouts.statusPaid')
  };

  const [statusFilter, setStatusFilter] = useState<SellerPayoutStatus | ''>('');
  const [page, setPage] = useState(1);
  const [transitioningPayout, setTransitioningPayout] = useState<IPayout | null>(null);
  const [toStatus, setToStatus] = useState<SellerPayoutStatus>(SellerPayoutStatus.CodReceived);
  const [note, setNote] = useState('');
  const [bankReference, setBankReference] = useState('');

  const { data, isLoading } = useGetPayoutsQuery({
    status: statusFilter === '' ? undefined : statusFilter,
    page,
    pageSize: DEFAULT_PAGE_SIZE
  });
  const [transitionPayout, { isLoading: isTransitioning }] = useTransitionPayoutMutation();
  const [exportPayouts] = useExportPayoutsMutation();

  const openTransitionDialog = (payout: IPayout) => {
    setTransitioningPayout(payout);
    // Default to the natural next step, admin can still pick a different
    // one (skip-ahead/backward) - the server requires a note for those.
    setToStatus(Math.min(payout.status + 1, SellerPayoutStatus.Paid));
    setNote('');
    setBankReference('');
  };

  const closeTransitionDialog = () => {
    setTransitioningPayout(null);
  };

  const submitTransition = () => {
    if (!transitioningPayout) {
      return;
    }

    transitionPayout({
      orderId: transitioningPayout.orderId,
      toStatus,
      note: note || undefined,
      bankReference: bankReference || undefined
    })
      .unwrap()
      .then(() => {
        showNotification(t('administration.payouts.updateSuccess'));
        closeTransitionDialog();
      })
      .catch((error: any) => {
        showNotification(getErrorMessage(error, t('administration.payouts.updateError')));
      });
  };

  const dataColumns: GridColDef[] = [
    {
      field: 'orderId',
      headerName: t('administration.payouts.orderNumber'),
      width: 100,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'sellerUsername',
      headerName: t('administration.payouts.seller'),
      width: 150,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'sellerBankDetails',
      headerName: t('administration.payouts.bankDetails'),
      width: 260,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params: GridValueGetterParams) => `${params.row.sellerIban ?? '-'} / ${params.row.sellerSwift ?? '-'}`
    },
    {
      field: 'amountOwed',
      headerName: t('administration.payouts.amountOwed'),
      width: 140,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params: GridValueGetterParams) => `${Number(params.value).toFixed(2)} EUR`
    },
    {
      field: 'status',
      headerName: t('administration.payouts.status'),
      width: 180,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params: GridValueGetterParams) => statusLabels[params.value as SellerPayoutStatus]
    },
    {
      field: 'operationCode',
      headerName: t('administration.payouts.speedyStatus'),
      width: 160,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params: GridValueGetterParams) =>
        params.value === DELIVERED_OPERATION_CODE ? t('administration.payouts.speedyDelivered') : '-'
    },
    {
      field: 'bankReference',
      headerName: t('administration.payouts.bankReference'),
      width: 160,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'actions',
      headerName: '',
      width: 160,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <Button onClick={() => { openTransitionDialog(params.row as IPayout); }}>
          {t('administration.payouts.changeStatus')}
        </Button>
      )
    }
  ];

  if (isLoading) {
    return <div>{t('common.loading')}</div>;
  }

  return (
    <div className="admin-payouts-tab">
      <div className="admin-payouts-tab-filters">
        <MuiFormControl variant="outlined" className="sort-container">
          <InputLabel id="payout-status-filter-label">{t('administration.payouts.status')}</InputLabel>
          <Select
            sx={{ width: '220px' }}
            labelId="payout-status-filter-label"
            value={statusFilter}
            label={t('administration.payouts.status')}
            onChange={(e) => {
              setStatusFilter(e.target.value as SellerPayoutStatus | '');
              setPage(1);
            }}
          >
            <MenuItem value="">
              <ListItemText primary={t('administration.payouts.statusAll')}/>
            </MenuItem>
            {Object.entries(statusLabels).map(([value, label]) => (
              <MenuItem key={value} value={Number(value)}>
                <ListItemText primary={label}/>
              </MenuItem>
            ))}
          </Select>
        </MuiFormControl>
        <Button onClick={() => {
          exportPayouts({ status: statusFilter === '' ? undefined : statusFilter })
            .unwrap()
            .catch(() => {
              showNotification(t('administration.payouts.exportError'));
            });
        }}>
          {t('administration.payouts.export')}
        </Button>
      </div>

      <DataGrid
        rows={data?.models ?? []}
        columns={dataColumns}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE }
          }
        }}
        pageSizeOptions={[DEFAULT_PAGE_SIZE]}
        rowCount={data?.totalCount ?? 0}
        paginationMode="server"
        onPaginationModelChange={(e) => {
          setPage(e.page + 1);
        }}
      />

      {transitioningPayout && (
        <Dialog open onClose={closeTransitionDialog} maxWidth="xs" fullWidth>
          <DialogTitle>
            {t('administration.payouts.dialogTitle', { orderId: transitioningPayout.orderId })}
          </DialogTitle>
          <DialogContent dividers className="admin-payouts-tab-dialog-content">
            {transitioningPayout.operationCode === DELIVERED_OPERATION_CODE && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('administration.payouts.deliveredHint')}
              </Alert>
            )}
            <MuiFormControl variant="outlined" fullWidth sx={{ mb: 2 }}>
              <InputLabel id="transition-to-status-label">{t('administration.payouts.newStatus')}</InputLabel>
              <Select
                labelId="transition-to-status-label"
                value={toStatus}
                label={t('administration.payouts.newStatus')}
                onChange={(e) => { setToStatus(e.target.value as SellerPayoutStatus); }}
              >
                {Object.entries(statusLabels).map(([value, label]) => (
                  <MenuItem key={value} value={Number(value)}>
                    <ListItemText primary={label}/>
                  </MenuItem>
                ))}
              </Select>
            </MuiFormControl>
            {toStatus === SellerPayoutStatus.Paid && (
              <TextField
                label={t('administration.payouts.bankReferenceRequired')}
                fullWidth
                required
                value={bankReference}
                onChange={(e) => { setBankReference(e.target.value); }}
                sx={{ mb: 2 }}
              />
            )}
            {toStatus !== transitioningPayout.status + 1 && (
              <TextField
                label={t('administration.payouts.noteRequired')}
                fullWidth
                multiline
                rows={2}
                value={note}
                onChange={(e) => { setNote(e.target.value); }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <MuiButton onClick={closeTransitionDialog}>{t('administration.payouts.cancel')}</MuiButton>
            <MuiButton disabled={isTransitioning} onClick={submitTransition}>
              {t('administration.payouts.save')}
            </MuiButton>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default AdminPayoutsTab;
